/**
 * @fileoverview Settings → Account: appearance, the retention promise, the
 * consent ledger, the resources, and the way out.
 *
 * Appearance leads because it is the one row here that changes what the
 * instructor is looking at. The choice is a preference of this browser, saved
 * locally and applied at once — see lib/theme.js for why it is not an account
 * field.
 *
 * Three former tiles (Privacy & Compliance, Resources, Sign out) as one card
 * of rows. The retention line stays because it is the promise every consent
 * text rests on; the consent ledger is the audit record behind it, so the
 * two sit on one row.
 */

import { SettingsLinkRow, SettingsRow, SettingsSection } from "@/components/settings/SettingsSection";
import ThemeToggle from "@/components/ThemeToggle";
import { Icon } from "@/components/studio/primitives";
import { supportEmail } from "@/lib/companyBrand";

/**
 * @param {object} props
 * @param {function} props.onConsentLedger Navigates to the ledger.
 * @param {function} props.onLogout Ends the session.
 */
export default function AccountSection({ onConsentLedger, onLogout }) {
  return (
    <SettingsSection title="Account" testId="account-section">
      <SettingsRow
        title="Appearance"
        desc="Light is the default. Dark suits a dim room. Saved on this device."
        trailing={<ThemeToggle />}
        testId="appearance-row"
      />
      <SettingsRow
        title="Recordings & privacy"
        desc="Full lesson recordings are deleted after processing succeeds or permanently fails. Audio awaiting retry is retained temporarily, with a seven-day cleanup backstop. Consented practice clips are kept separately for up to 30 days."
        trailing={
          <button
            type="button"
            onClick={onConsentLedger}
            data-testid="consent-ledger-link"
            className="inline-flex items-center gap-1 whitespace-nowrap font-sans text-[13px] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
          >
            Consent ledger <Icon name="chevronRight" size={14} />
          </button>
        }
      />
      <SettingsLinkRow href="/guide" label="Hans Academy" sub="Guides, tips, and how-to articles" />
      <SettingsLinkRow href={`mailto:${supportEmail()}`} label="Contact support" sub={supportEmail()} />
      <SettingsLinkRow href="/security" label="Security" />
      <SettingsLinkRow href="/privacy" label="Data privacy" />
      <button
        type="button"
        onClick={onLogout}
        data-testid="logout-button"
        className="flex w-full items-center gap-2 px-5 py-4 text-left font-sans text-[15px] text-[var(--text-secondary)] transition-colors hover:bg-[var(--studio-overlay)] hover:text-[var(--text-primary)] md:px-6 touch-target"
      >
        <Icon name="logout" size={16} /> Sign out
      </button>
    </SettingsSection>
  );
}
